/**
 * Which value editor a deal field needs in the deals list's Bulk Edit dialog,
 * or null when the dialog cannot edit it (feedback #42).
 *
 * WHICH fields are offered is data -- the `bulk_editable` metadata flag (de-hub
 * migration 114). This only answers whether the dialog has an editor for the
 * field's kind: a dropdown needs an inline `options` array (async lookups such
 * as brokerage_id have none), and a date is any date-only field.
 *
 * @param {object} m — a deals ui-metadata row
 * @returns {"select" | "date" | null}
 */
export const bulkEditKind = (m) => {
  const input = (m?.input_type || "").toLowerCase();
  if (input === "select") {
    return Array.isArray(m.options) && m.options.length > 0 ? "select" : null;
  }
  if (input === "date" || m?.data_type === "Date") return "date";
  return null;
};

/**
 * The fields the Bulk Edit dialog offers, in metadata order.
 *
 * Switched on by `bulk_editable`, visible, editable, of a kind the dialog can
 * edit, and writable by this caller. Against a de-hub that predates the flag
 * (no row carries it) it falls back to the old rule -- dropdowns only -- so
 * the app can ship before or after migration 114.
 *
 * @param {object[]} metadata — deals ui-metadata rows
 * @param {(field: string, row: object) => boolean} canWriteField
 * @returns {{ value: string, label: string, kind: "select"|"date", options?: object[] }[]}
 */
export const bulkEditFields = (metadata, canWriteField) => {
  const rows = Array.isArray(metadata) ? metadata : [];
  const hasFlag = rows.some((m) => m && "bulk_editable" in m);
  return rows
    .map((m) => ({ m, kind: bulkEditKind(m) }))
    .filter(
      ({ m, kind }) =>
        kind !== null &&
        !m.hide &&
        m.editable !== false &&
        (hasFlag ? m.bulk_editable === true : kind === "select")
    )
    .filter(({ m }) => canWriteField(m.field, m))
    .map(({ m, kind }) => ({
      value: m.field,
      label: m.label || m.field,
      kind,
      options: m.options,
    }));
};
